import { useState } from 'react';
import styled from 'styled-components';

import { colors } from 'styles/theme/colors';
import { BodySmall, BodySmallBold } from 'styles/typography';

import { benefitCoverageText, benefitDescriptionText, partitionStcGroups, StcGroup, Stedi271 } from './eligibilityData';

// The benefits table for a completed check: raw 271 benefit lines grouped by service-type code.
// Skilled nursing (AG/AH) is pulled into its own section, and — since a real 271 can carry 30+
// STCs — only the priority coverages (Health Benefit Plan, Medical) show expanded; the rest
// collapse behind a toggle.

interface Props {
  stedi: Stedi271;
  planName?: string;
}

const COLUMNS = ['Type', 'Coverage Level', 'Network', 'Coverage', 'Benefit'];

function StcTable({ group }: { group: StcGroup }) {
  return (
    <Group>
      <GroupTitle>
        STC {group.stcCode}
        {group.stcName ? ` ${group.stcName}` : ''}
      </GroupTitle>
      <TableScroll>
        <Table>
          <thead>
            <tr>
              {COLUMNS.map((col) => (
                <Th key={col}>{col}</Th>
              ))}
            </tr>
          </thead>
          <tbody>
            {group.lines.map((line, i) => (
              <tr key={i}>
                <Td>{line.name || '—'}</Td>
                <Td>{line.coverageLevel || '—'}</Td>
                <Td>{line.inPlanNetworkIndicator || '—'}</Td>
                <Td>{benefitCoverageText(line)}</Td>
                <Td>{benefitDescriptionText(line)}</Td>
              </tr>
            ))}
          </tbody>
        </Table>
      </TableScroll>
    </Group>
  );
}

export default function BenefitsByStc({ stedi, planName }: Props) {
  const [showAll, setShowAll] = useState(false);
  const { priority, rest } = partitionStcGroups(stedi.benefitsInformation);
  if (priority.length === 0 && rest.length === 0) return null;

  return (
    <Section data-testid='eligibility-benefits'>
      <SectionHeader>
        <span>Benefits{planName ? ` – ${planName}` : ''}</span>
      </SectionHeader>

      {priority.map((group) => (
        <StcTable key={group.stcCode} group={group} />
      ))}

      {rest.length > 0 && (
        <>
          {showAll && rest.map((group) => <StcTable key={group.stcCode} group={group} />)}
          <ToggleRow>
            <Toggle type='button' onClick={() => setShowAll((v) => !v)} data-testid='toggle-all-benefits'>
              {showAll ? 'Show fewer benefits' : `Show all benefits (${rest.length} more)`}
            </Toggle>
          </ToggleRow>
        </>
      )}
    </Section>
  );
}

const Section = styled.div`
  border: 1px solid ${colors.black10};
  border-radius: 8px;
  overflow: hidden;
`;

const SectionHeader = styled(BodySmallBold)`
  display: flex;
  justify-content: space-between;
  padding: 16px 24px;
  font-size: 15px;
  background: ${colors.blue5};
  border-bottom: 1px solid ${colors.black10};
  color: ${colors.black100};
`;

const Group = styled.div`
  padding: 20px 24px;

  & + & {
    border-top: 1px solid ${colors.black10};
  }
`;

const GroupTitle = styled(BodySmallBold)`
  display: block;
  margin-bottom: 12px;
  color: ${colors.black100};
`;

const TableScroll = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: left;
  padding: 8px 12px;
  font-size: 12px;
  font-weight: 400;
  color: ${colors.black50};
  border-bottom: 1px solid ${colors.black10};
  white-space: nowrap;
`;

const Td = styled(BodySmall).attrs({ as: 'td' })`
  padding: 12px;
  vertical-align: top;
  color: ${colors.black100};
  border-bottom: 1px solid ${colors.black5};
`;

const ToggleRow = styled.div`
  padding: 16px 24px;
  border-top: 1px solid ${colors.black10};
`;

const Toggle = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: ${colors.blue100};
  font-weight: 600;
`;
